/**
 * Minimal in-memory LRU cache with optional per-entry TTL. Bounded by `max` entries
 * (evicts least-recently-used on overflow); expired entries are dropped lazily on access.
 * Browser-only and per-tab — cleared on reload. Not for sensitive data.
 *
 * @template T
 */
export class LRUCache {
  /** @param {{ max?: number, ttl?: number }} [options] max entries; default ttl in ms (0 = never). */
  constructor({ max = 50, ttl = 0 } = {}) {
    this.max = Math.max(1, max);
    this.defaultTtl = Math.max(0, ttl);
    /** @type {Map<any, { value: T, expiresAt: number }>} */
    this.store = new Map();
  }

  // Returns the value or undefined; touching a live entry marks it most-recently-used.
  get(key) {
    const entry = this.store.get(key);
    if (!entry) return undefined;

    if (entry.expiresAt !== 0 && entry.expiresAt <= Date.now()) {
      this.store.delete(key);
      return undefined;
    }

    // Re-insert to move it to the end (most-recently-used).
    this.store.delete(key);
    this.store.set(key, entry);
    return entry.value;
  }

  has(key) {
    return this.get(key) !== undefined;
  }

  // Stores a value, evicting the least-recently-used entry when at capacity.
  set(key, value, ttl = this.defaultTtl) {
    if (this.store.has(key)) {
      this.store.delete(key);
    } else if (this.store.size >= this.max) {
      const oldestKey = this.store.keys().next().value;
      if (oldestKey !== undefined) {
        this.store.delete(oldestKey);
      }
    }

    const expiresAt = ttl > 0 ? Date.now() + ttl : 0;
    this.store.set(key, { value, expiresAt });
    return value;
  }

  delete(key) {
    return this.store.delete(key);
  }

  clear() {
    this.store.clear();
  }

  get size() {
    return this.store.size;
  }
}

export default LRUCache;
